export class PaymentHistoryService {
  static getOverrideAmount(overrideData, loanId, bookIndex) {
    if (!overrideData) return null;

    // Check for individual amounts by loan ID
    if (
      overrideData.individualAmounts &&
      overrideData.individualAmounts[loanId] !== undefined
    ) {
      return parseFloat(overrideData.individualAmounts[loanId]) || 0;
    }

    // Check for override amounts by book index
    if (
      overrideData.overrideAmounts &&
      overrideData.overrideAmounts[bookIndex] !== undefined
    ) {
      return parseFloat(overrideData.overrideAmounts[bookIndex]) || 0;
    }

    return null;
  }

  static isLoanSelectedInOverride(overrideData, loanId, bookIndex) {
    if (!overrideData || !overrideData.selectedBooks) return false;
    return overrideData.selectedBooks.includes(bookIndex);
  }

  static getActualReturnDate(metadata) {
    // Priority order for return date:
    // 1. Explicit return_date if exists
    // 2. transaction_date if the state is ITEM_RETURNED
    // 3. updated date as fallback

    if (metadata.return_date) {
      return metadata.return_date;
    }

    if (metadata.state === "ITEM_RETURNED" && metadata.transaction_date) {
      return metadata.transaction_date;
    }

    // Use updated date as last resort, but only if it's different from end_date
    if (metadata.updated && metadata.updated !== metadata.end_date) {
      return metadata.updated;
    }

    // If we can't determine return date, use current date
    return new Date().toISOString();
  }

  static isLoanOverdue(dueDate, returnDate) {
    if (!dueDate || !returnDate) return false;

    const due = new Date(dueDate);
    const returned = new Date(returnDate);

    return returned > due;
  }

  static calculateFineForLoan(startDate, endDate, returnDate) {
    if (!endDate || !returnDate) return 0;

    const due = new Date(endDate);
    const returned = new Date(returnDate);

    if (returned <= due) return 0;

    // Calculate business days (excluding weekends)
    let count = 0;
    const currentDate = new Date(due);
    currentDate.setDate(currentDate.getDate() + 1);

    while (currentDate <= returned) {
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        count++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return count * 10; // 10 pesos per business day
  }

  static calculateDaysOverdue(dueDate, returnedDate) {
    if (!dueDate || !returnedDate) return 0;

    const due = new Date(dueDate);
    const returned = new Date(returnedDate);

    if (returned <= due) return 0;

    // Calculate business days (excluding weekends)
    let count = 0;
    const currentDate = new Date(due);
    currentDate.setDate(currentDate.getDate() + 1);

    while (currentDate <= returned) {
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        count++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return count;
  }

  static formatDate(dateString) {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  }

  static processPaymentHistory(loans, overrideData) {
    const processedLoans = loans.map((loan, index) => {
      const metadata = loan.metadata;
      const loanId = metadata.pid;

      // Only process returned loans, so always use actual return date
      const actualReturnDate = this.getActualReturnDate(metadata);

      // Use the same due date field as other components
      const dueDate =
        metadata.end_date || metadata.due_date || metadata.dueDate;

      // Calculate fine for overdue loan
      const calculatedFine = this.calculateFineForLoan(
        metadata.start_date,
        dueDate,
        actualReturnDate
      );

      // Get override amount if available
      const overrideAmount = this.getOverrideAmount(
        overrideData,
        loanId,
        index
      );
      const isOverridden = overrideAmount !== null;

      // PRIORITY: Use stored payment amount first, then override, then calculated
      let finalAmount = calculatedFine;
      let actualPaidAmount = metadata.payment_amount;

      // If there's a stored payment_amount, use that as the final amount
      if (actualPaidAmount !== undefined && actualPaidAmount !== null) {
        finalAmount = parseFloat(actualPaidAmount) || 0;
      }
      // Otherwise, use override amount if available
      else if (isOverridden) {
        finalAmount = overrideAmount;
      }

      // Check if this loan is selected in override data
      const isSelectedInOverride = this.isLoanSelectedInOverride(
        overrideData,
        loanId,
        index
      );

      // Determine if this amount was overridden during payment submission
      const wasOverriddenInSubmission =
        (actualPaidAmount !== undefined &&
          actualPaidAmount !== null &&
          actualPaidAmount !== calculatedFine) ||
        (isOverridden && overrideAmount !== calculatedFine) ||
        metadata.override_data; // Check if override_data was stored during payment

      // Safely handle document authors
      const documentAuthors = metadata.document?.authors;
      const safeAuthors = Array.isArray(documentAuthors) ? documentAuthors : [];

      const processedLoan = {
        loanId: metadata.pid,
        patronName: metadata.patron?.name || "Unknown",
        patronEmail: metadata.patron?.email || "Unknown",
        documentTitle: metadata.document?.title || "Unknown Document",
        documentAuthors: safeAuthors,
        borrowedDate: this.formatDate(metadata.start_date),
        dueDate: this.formatDate(dueDate),
        returnedDate: this.formatDate(actualReturnDate),
        returnedDateRaw: actualReturnDate,
        paymentAmount: finalAmount,
        originalCalculatedFine: calculatedFine,
        overrideAmount: overrideAmount,
        isOverridden: wasOverriddenInSubmission,
        isSelectedInOverride: isSelectedInOverride,
        finesPaid: metadata.fines_paid || false,
        paymentReceipt: metadata.payment_receipt,
        itemBarcode: metadata.item?.barcode || "Unknown",
        transactionId: metadata.transaction_location_pid,
        paymentDate: this.formatDate(metadata.payment_date || actualReturnDate),
        loanState: metadata.state,
        daysOverdue: this.calculateDaysOverdue(dueDate, actualReturnDate),
        overrideData: metadata.override_data,
      };

      return processedLoan;
    });

    // Sort by return date (most recent first)
    return processedLoans.sort((a, b) => {
      const dateA = new Date(a.returnedDateRaw);
      const dateB = new Date(b.returnedDateRaw);
      return dateB - dateA;
    });
  }

  static downloadReceipt(receiptData, fileName) {
    if (!receiptData) return;

    try {
      if (typeof receiptData === "string" && receiptData.startsWith("http")) {
        window.open(receiptData, "_blank");
        return;
      }

      const link = document.createElement("a");
      link.href = receiptData;
      link.download = fileName || "payment_receipt";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading receipt:", error);
    }
  }
}
